import React, { useState, useEffect } from 'react';
import { Users, FileText, DollarSign, MoreVertical, Eye, Download, RefreshCw } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import HeaderTopSection from "../../components/partials/HeaderTop";
import '../../assets/css/DashboardPage.css';
import Banner from "../../components/partials/Banner";
import StatisticsService from '../../services/StatisticsService';
import LoadingSpinner from '../../utils/LoadingSpinner';

const DashboardPage = () => {
    const [timeRange, setTimeRange] = useState('7d');
    const [isLoading, setIsLoading] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    const [loadingStartTime, setLoadingStartTime] = useState(0);
    const [dashboardData, setDashboardData] = useState({
        stats: null,
        subscriptionEvolution: [],
        professionalDistribution: [],
        recentActivities: []
    });

    // Charger les données au montage du composant et quand timeRange change
    useEffect(() => {
        loadDashboardData();
    }, [timeRange]);

    const loadDashboardData = async () => {
        const startTime = Date.now();
        setLoadingStartTime(startTime);
        setShowLoading(true);
        setIsLoading(true);

        try {
            let startDate, endDate;

            // Déterminer la période en fonction de timeRange
            switch (timeRange) {
                case '7d':
                    ({ startDate, endDate } = StatisticsService.getDefaultPeriod());
                    break;
                case '30d':
                    ({ startDate, endDate } = StatisticsService.getLast30DaysPeriod());
                    break;
                case '90d':
                    ({ startDate, endDate } = StatisticsService.getLast3MonthsPeriod());
                    break;
                default:
                    ({ startDate, endDate } = StatisticsService.getDefaultPeriod());
            }

            const data = await StatisticsService.getCompleteDashboardData(startDate, endDate);

            // Calculer le temps écoulé
            const elapsedTime = Date.now() - startTime;
            const minimumLoadingTime = 3000; // 3 secondes minimum

            // Si le chargement a pris moins de 3 secondes, attendre le temps restant
            if (elapsedTime < minimumLoadingTime) {
                const remainingTime = minimumLoadingTime - elapsedTime;
                await new Promise(resolve => setTimeout(resolve, remainingTime));
            }

            setDashboardData(data);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            // Vous pouvez afficher un message d'erreur à l'utilisateur ici
        } finally {
            setIsLoading(false);
            setShowLoading(false);
        }
    };

    const handleRefresh = () => {
        loadDashboardData();
    };

    // Préparer les données pour les cartes de statistiques
    const statsData = dashboardData.stats ? [
        {
            title: 'Clients Actifs',
            value: dashboardData.stats.activeClients?.toLocaleString() || '0',
            icon: Users,
            color: '#3b82f6',
            bgColor: '#eff6ff'
        },
        {
            title: 'Contrats Signés',
            value: dashboardData.stats.signedContracts?.toLocaleString() || '0',
            icon: FileText,
            color: '#10b981',
            bgColor: '#f0fdf4'
        },
        {
            title: 'Revenus',
            value: `FCFA ${dashboardData.stats.totalRevenue?.toLocaleString() || '0'}`,
            icon: DollarSign,
            color: '#8b5cf6',
            bgColor: '#faf5ff'
        },
        {
            title: 'Employés Actifs',
            value: dashboardData.stats.activeProfessionals?.toLocaleString() || '0',
            icon: Users,
            color: '#f59e0b',
            bgColor: '#fffbeb'
        }
    ] : [];

    // Préparer les données pour les graphiques
    const chartData = dashboardData.subscriptionEvolution ?
        dashboardData.subscriptionEvolution.map(item => ({
            name: item.period,
            users: item.users || 0,
            contracts: item.contracts || 0,
            revenue: item.revenue || 0
        })) : [];

    const pieData = dashboardData.professionalDistribution ?
        dashboardData.professionalDistribution.map(item => ({
            name: item.category,
            value: item.count || 0, // Utiliser count au lieu de percentage
            color: item.color
        })) : [];

    const recentActivities = dashboardData.recentActivities || [];

    const buttonStyle = {
        padding: '0.5rem',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer'
    };

    const iconButtonStyle = {
        padding: '0.25rem',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0.25rem',
        cursor: 'pointer'
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Tableau de bord"
                backgroundImage="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
            />

            {/* Loading Spinner */}
            <LoadingSpinner
                isVisible={showLoading}
                loadingText="Chargement des données en cours..."
            />

            <div className="dashboard-container">
                {/* Header Dashboard */}
                <div className="dashboard-header">
                    <div className="dashboard-header-content">
                        <div>
                            <h1 className="dashboard-header-title">Tableau de Bord</h1>
                            <p className="dashboard-header-subtitle">Vue d'ensemble de vos données et statistiques</p>
                        </div>
                        <div className="dashboard-header-controls">
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                className="dashboard-select"
                                disabled={isLoading}
                            >
                                <option value="7d">7 derniers jours</option>
                                <option value="30d">30 derniers jours</option>
                                <option value="90d">3 derniers mois</option>
                            </select>
                            <button
                                onClick={handleRefresh}
                                disabled={isLoading}
                                className="dashboard-refresh-button"
                            >
                                <RefreshCw style={{
                                    width: '1rem',
                                    height: '1rem',
                                    animation: isLoading ? 'spin 1s linear infinite' : 'none'
                                }} />
                                <span>Actualiser</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="dashboard-content">
                    {/* Stats Cards */}
                    <div className="dashboard-stats-grid">
                        {statsData.map((stat, index) => (
                            <div
                                key={index}
                                className="dashboard-stat-card"
                            >
                                <div className="dashboard-stat-card-header">
                                    <div className="dashboard-stat-icon" style={{ backgroundColor: stat.bgColor }}>
                                        <stat.icon style={{ width: '1.5rem', height: '1.5rem', color: stat.color }} />
                                    </div>
                                    <button style={iconButtonStyle}>
                                        <MoreVertical style={{ width: '1rem', height: '1rem', color: '#9ca3af' }} />
                                    </button>
                                </div>
                                <div>
                                    <h3 className="dashboard-stat-title">{stat.title}</h3>
                                    <p className="dashboard-stat-value">{stat.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Charts Row */}
                    <div className="dashboard-charts-grid">
                        {/* Area Chart - Évolution des souscriptions */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Évolution des souscriptions</h3>
                                    <p className="dashboard-chart-subtitle">Croissance sur les {timeRange === '7d' ? '7' : timeRange === '30d' ? '30' : '90'} derniers jours</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <button style={buttonStyle}>
                                        <Eye style={{ width: '1rem', height: '1rem', color: '#6b7280' }} />
                                    </button>
                                    <button style={buttonStyle}>
                                        <Download style={{ width: '1rem', height: '1rem', color: '#6b7280' }} />
                                    </button>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" stroke="#9CA3AF" />
                                    <YAxis stroke="#9CA3AF" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="users"
                                        stroke="#3B82F6"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#colorUsers)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Pie Chart - Répartition des employés */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Répartition des employés</h3>
                                    <p className="dashboard-chart-subtitle">Par statut d'activité</p>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="dashboard-pie-chart-legend">
                                {pieData.map((item, index) => (
                                    <div key={index} className="dashboard-legend-item">
                                        <div className="dashboard-legend-label">
                                            <div className="dashboard-legend-color" style={{ backgroundColor: item.color }}></div>
                                            <span>{item.name}</span>
                                        </div>
                                        <span className="dashboard-legend-value">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Row */}
                    <div className="dashboard-bottom-grid">
                        {/* Bar Chart - Revenus par Jour */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Revenus par Jour</h3>
                                    <p className="dashboard-chart-subtitle">Évolution des revenus</p>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" stroke="#9CA3AF" />
                                    <YAxis stroke="#9CA3AF" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                    <Bar dataKey="revenue" fill="#10B981" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Recent Activities */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Activités Récentes</h3>
                                    <p className="dashboard-chart-subtitle">Dernières actions des utilisateurs</p>
                                </div>
                                <button style={{
                                    color: '#2563eb',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}>
                                    Voir tout
                                </button>
                            </div>
                            <div className="dashboard-activities-list">
                                {recentActivities.map((activity) => (
                                    <div
                                        key={activity.id}
                                        className="dashboard-activity-item"
                                    >
                                        <div className="dashboard-activity-avatar">
                                            {activity.avatar}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p className="dashboard-activity-text">
                                                <span style={{ fontWeight: '500' }}>{activity.user}</span> {activity.action}
                                            </p>
                                            <p className="dashboard-activity-time">{activity.time}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DashboardPage;